// Shared fetch helper for the newer "scoped API module" convention
// (api/walletIntel.ts, api/profiler.ts, and any future one) — previously each
// module hand-rolled its own near-identical copy of this with its own
// immediate-redirect-on-401 logic. Consolidated here so both get the same
// silent-refresh-before-logout behavior as the main ApiClient in lib/api.ts
// (see lib/tokenRefresh.ts for why: a 30-minute access token with no refresh
// path meant any session running longer than that hard-logged-out mid-task).
import { getAccessToken, refreshAccessToken, forceLogout, isImpersonating, IMPERSONATION_ENDED_EVENT } from '@/lib/tokenRefresh';

export interface ApiRequestOptions extends RequestInit {
  responseType?: 'blob' | 'json';
  /** On a 401 that survives a silent-refresh attempt, throw an AuthError
   * instead of hard-redirecting to /login — for call sites (e.g. a case
   * detail fetch that might 404-as-401 on a shared link) that want to
   * handle "not authenticated" gracefully instead of yanking the user away. */
  noRedirect?: boolean;
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
  _isRetry = false,
): Promise<T> {
  const token = getAccessToken();
  const { responseType, noRedirect, ...fetchOpts } = options;
  // A FormData body (file uploads) must NOT get a hardcoded JSON
  // Content-Type — the browser sets its own multipart boundary when the
  // header is left unset, which breaks if we force one here.
  const isFormData = typeof FormData !== 'undefined' && fetchOpts.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(fetchOpts.headers as Record<string, string>),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`/api${path}`, { ...fetchOpts, headers });

  if (res.status === 401 && !_isRetry && token) {
    const newToken = await refreshAccessToken();
    if (newToken) return apiRequest<T>(path, options, true);
  }

  if (!res.ok) {
    if (res.status === 401) {
      if (noRedirect) {
        const err = new Error('Session expired — please re-authenticate');
        err.name = 'AuthError';
        throw err;
      }
      // Impersonation tokens are deliberately non-refreshable (see
      // startImpersonation in this file) — a 401 here must fall back to the
      // admin's own session instead of forceLogout()'ing the whole browser
      // tab. See IMPERSONATION_ENDED_EVENT's doc comment for the full story.
      if (isImpersonating()) {
        window.dispatchEvent(new Event(IMPERSONATION_ENDED_EVENT));
      } else {
        forceLogout();
      }
    }
    const text = await res.text();
    try {
      const j = JSON.parse(text);
      // Pydantic validation errors return detail as an array of {loc, msg, type}
      const detail = Array.isArray(j.detail)
        ? j.detail.map((d: { loc?: string[]; msg?: string }) =>
            `${d.loc?.slice(-1)?.[0] ?? 'field'}: ${d.msg ?? 'invalid'}`
          ).join('; ')
        : j.detail || j.message || `${res.status} ${res.statusText}`;
      throw new Error(detail);
    } catch (e) {
      if (e instanceof Error && !(e instanceof SyntaxError)) throw e;
      throw new Error(`API Error: ${res.status} ${res.statusText}`);
    }
  }

  if (responseType === 'blob') return res.blob() as Promise<T>;
  return res.json() as Promise<T>;
}
